import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { joinWaitlist } from '../api';
import { useAuth, PLAN_META } from '../context/AuthContext';
import { useToast } from '../App';
import { LayoutDashboard, SettingsIcon, Target } from './Icons';
import './Navbar.css';

const NAV_LINKS = [
  { to: '/app/scope', label: 'Scope Analysis', Icon: Target, end: true },
  { to: '/app', label: 'Dashboard', Icon: LayoutDashboard, end: true },
  { to: '/app/settings', label: 'Settings', Icon: SettingsIcon, end: false },
];

export default function Navbar() {
  const { user, profile, logout, planMeta } = useAuth();
  const { addToast } = useToast();
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const planInfo = planMeta || PLAN_META.starter;
  const currentPlan = profile?.plan || 'starter';
  const targetPlan = currentPlan === 'starter' ? 'pro' : 'teams';
  const targetLabel = currentPlan === 'starter' ? 'Pro — $12/mo' : 'Teams — $29/mo';

  async function handleUpgradeRequest() {
    setSubmitting(true);
    try {
      await joinWaitlist({
        email: user?.email,
        plan: targetPlan,
        source: 'in-app-navbar',
      });
    } catch {
      // Silently succeed — waitlist may already have this email
    } finally {
      setSubmitting(false);
      setShowUpgrade(false);
      addToast(`Got it! We'll reach out to ${user?.email} to complete your upgrade.`, 'success');
    }
  }

  return (
    <>
      <aside className="navbar">
        {/* Logo */}
        <div className="navbar-logo">
          <span className="brand-logo-text">fenscope.</span>
        </div>

        {/* User info */}
        {user && (
          <div className="navbar-user">
            <div className="navbar-user-avatar">
              {profile?.avatar_url
                ? <img src={profile.avatar_url} alt="avatar" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                : (profile?.name || user.email || 'U').slice(0, 2).toUpperCase()
              }
            </div>
            <div className="navbar-user-info">
              <div className="navbar-user-name">{profile?.name || user.email}</div>
              <span className="navbar-plan-badge" style={{ '--plan-color': planInfo.color }}>
                {planInfo.badge}
              </span>
            </div>
          </div>
        )}

        {/* Navigation Links */}
        <nav className="navbar-nav">
          <div className="navbar-nav-label">Navigation</div>
          {NAV_LINKS.map(link => {
            const Icon = link.Icon;
            return (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.end}
                className={({ isActive }) => `navbar-link ${isActive ? 'active' : ''}`}
              >
                <Icon size={17} className="navbar-link-icon" />
                <span>{link.label}</span>
              </NavLink>
            );
          })}
        </nav>

        {/* Upgrade banner for non-teams users */}
        {user && currentPlan !== 'teams' && (
          <div className="navbar-upgrade" style={{ marginTop: '20px' }}>
            <div className="navbar-upgrade-title">
              {currentPlan === 'starter' ? 'Upgrade to Pro' : 'Upgrade to Teams'}
            </div>
            <p className="navbar-upgrade-desc">
              {currentPlan === 'starter'
                ? 'Unlock 5 projects, unlimited checks, custom tones & Scope Ledger.'
                : 'Get unlimited projects, ledger export, team hub & priority support.'}
            </p>
            <button
              className="btn btn-primary"
              style={{ width: '100%', fontSize: '0.8rem', padding: '8px 14px' }}
              onClick={() => setShowUpgrade(true)}
            >
              Get {targetLabel}
            </button>
          </div>
        )}

        {/* Connect Extension CTA */}
        {/* {user && (
          <div className="navbar-ext-cta">
            <span>🧩</span>
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.78rem', marginBottom: 2 }}>Chrome Extension</div>
              <a
                href="/ext-login"
                target="_blank"
                rel="noreferrer"
                style={{ fontSize: '0.73rem', color: 'var(--accent)', textDecoration: 'none', fontWeight: 500 }}
              >
                Connect extension →
              </a>
            </div>
          </div>
        )} */}

        <div className="navbar-spacer" />

        {/* Sign out */}
        {user && (
          <button className="navbar-signout" onClick={logout}>
            Sign out
          </button>
        )}
      </aside>

      {/* Upgrade Modal */}
      {showUpgrade && (
        <div className="modal-overlay" onClick={() => setShowUpgrade(false)}>
          <div className="modal-panel" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <h2>Upgrade to {currentPlan === 'starter' ? 'Pro' : 'Teams'}</h2>
              <button className="modal-close-btn" onClick={() => setShowUpgrade(false)}>&#x2715;</button>
            </div>

            <div style={{ padding: '4px 0 16px' }}>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: 16 }}>
                {currentPlan === 'starter'
                  ? 'Pro gives you 5 projects, unlimited scope checks, custom tones, and the full scope ledger.'
                  : 'Teams gives you unlimited projects, a team collaboration hub, and priority support.'}
              </p>

              <div style={{
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius)',
                padding: '10px 14px',
                marginBottom: 16,
              }}>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', marginBottom: 2 }}>Google account</div>
                <div style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--text-primary)' }}>{user?.email}</div>
              </div>

              <p style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', marginBottom: 20 }}>
                We'll send upgrade instructions to this email. Payment setup will follow via a secure link.
              </p>
            </div>

            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowUpgrade(false)}>Cancel</button>
              <button
                className="btn btn-primary btn-lg"
                onClick={handleUpgradeRequest}
                disabled={submitting}
              >
                {submitting ? <><div className="spinner" /> Sending…</> : `Request Upgrade → ${targetLabel}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
